/* ============================================================================
   SVNTY — SEARCH (recent searches · Arabic normalisation and the FOLD FALLBACK ·
   the iOS focus law · the prefix guard · the trouble watchdog)
   SVNTY, 2026-09-19, dev1.0. Classic deferred script, no module,
   no dependencies. Loaded ONCE, from snippets/header-search.liquid — which the
   header renders twice, which is what the `window.svntySearch` guard below is for.
   The search RESULTS page does not tag this file: it borrows the header's copy,
   because the header is on every page. (R2-49: the old header claimed
   sections/main-search.liquid loaded it. It never did.)

   WHY THESE FIVE THINGS AND NOT MORE
   1. RECENT SEARCHES live in localStorage only (`svnty:search:recent`, max 6).
      They render into <svnty-recent-searches>, which sits OUTSIDE
      `[data-predictive-search]` — the one element Trade's predictive-search.js
      overwrites. The sibling paid for this with a MutationObserver (TRAPS SH-06:
      "recent searches disappear the second time I open search"); designing the
      block out of the morph's reach is the same fix without the observer.
      Storage failure is a STATE, not a bug (TRAPS IOS-18): the block stays hidden.

   2. ══ ARABIC: TWO FUNCTIONS, NOT ONE (R2-12, the round-2 reversal) ══
      Round 1 shipped ONE `normalize()` that folded hamza forms, ta-marbuta and
      alef-maksura, and applied it to every path. Round 2 measured what that cost:

          /search?q=أبيض   ->  9 products        folded to ابيض  ->  0
          /search/suggest  ->  7 options         folded          ->  0

      Shopify's index is the HAYSTACK and it is not folded; folding only the
      NEEDLE therefore destroys real result sets. The fold is not wrong — it wins
      on tashkeel and tatweel, where the shopper's typing carries marks the index
      does not:

          q=بُولُو (tashkeel) -> 0    q=بولو -> 24    <- the strip is load-bearing
          q=بــولو (tatweel)  -> 0    q=بولو -> 24    <- so is this one

      So the two jobs are split:
        · normalize()  — SAFE and always applied. Strips tashkeel and tatweel and
          collapses whitespace. It never changes a LETTER, so it can never turn a
          9-result query into a 0-result one.
        · fold()       — the letter folds (hamza forms, ta-marbuta -> ha,
          alef-maksura / farsi yeh -> ya, keheh -> kaf). It is a FALLBACK: it runs
          only after the raw query has come back with nothing. The typeahead
          retries with it automatically (assets/predictive-search.js); the results
          page offers it as one link under the empty state
          (sections/main-search.liquid, `svnty.search.did_you_mean`).
      Under THE ARABIC LAW this matters more here than anywhere: hamza placement
      is exactly what an Egyptian shopper varies, and nothing is translated.

   3. THE FOCUS LAW (TRAPS IOS-20): on a coarse pointer the sheet focuses its
      CLOSE button, never the field, or the keyboard covers the sheet it opened.
      Trade's details-modal.js focuses the input on open; the `toggle` event fires
      after that, which is where this corrects it.

   4. ══ THE PREFIX GUARD (was THE PRODUCT-ONLY GUARD — R2-13) ══
      Round 1 welded `type=product` onto every search path. Round 2 measured what
      an Egyptian menswear store is actually asked, and the guard was answering
      the empty state over pages that exist:

          q=returns   typed 0 / untyped 2 pages      q=shipping  0 / 3
          q=contact         0 / 5                    q=exchange  0 / 2

      The reason the guard existed — "1 result over an empty grid" — does not
      apply to this theme: sections/main-search.liquid renders PAGE and ARTICLE
      cards as well as product cards (its `{% when 'page' %}` branch), so an
      untyped result set is fully drawn. `type` is therefore gone from every path,
      and what these forms still need carried is only `options[prefix]=last`,
      which Trade's facets.liquid also drops on a filter or a sort.

   5. THE TROUBLE WATCHDOG (the sibling TRAPS JS-42): Trade closes the panel silently on
      a failed fetch. Four seconds without an answer earns a sentence and a retry.
      R2-15: it used to be disarmed by the exact failure it was built for — a
      failed fetch called `close()`, which removed `loading`, which disarmed the
      timer, so only a slow-but-alive request ever showed it. It now disarms on
      `results` (a real answer) and FIRES AT ONCE when `loading` goes away with no
      answer behind it.
   ============================================================================ */
(function () {
  'use strict';

  /* WORDS (H4): from `window.svntyStrings` (snippets/svnty-head.liquid, rendered by Liquid from
     `locales/*.json`). No English literal lives in this file. */
  var STR = window.svntyStrings || {};

  /* R2-45: a STRING pattern in String.prototype.replace substitutes the first occurrence only, and
     `$&`, `` $` ``, `$'` and `$1` inside the VALUE are read as replacement patterns. A shopper's own
     search term travels through this path, so the token swap uses split/join, which has no pattern
     language at all. */
  function fill(key, token, value) {
    return String(STR[key] || '').split(token).join(String(value));
  }

  // Trade's header renders `header-search` TWICE (the drawer's sheet and the desktop
  // sheet), so this file's <script> tag is in the document twice and would otherwise
  // run twice and bind every delegated listener twice. A process-lifetime flag, the
  // shape the sibling uses where no custom element can carry the guard (TRAPS JS-01/JS-04).
  if (window.svntySearch) return;

  var STORAGE_KEY = 'svnty:search:recent';
  var MAX_ITEMS = 6;
  var MAX_LENGTH = 80;
  var TROUBLE_MS = 4000;

  /* -----------------------------------------------------------------------
     ARABIC — the safe normalisation, the fallback fold, and direction
     ----------------------------------------------------------------------- */
  var TASHKEEL = /[ً-ٰٟۖ-ۭ]/g; /* harakat, sukun, superscript alef */
  var TATWEEL = /ـ/g; /* kashida: pure decoration, never meaning */
  var ALEF = /[آأإٱ]/g; /* aa, a-hamza-above, a-hamza-below, alef wasla */
  var ARABIC_LETTER = /[ؠ-يٮ-ۓݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;

  /* SAFE. Marks and decoration only — every letter survives, so the result set cannot shrink. */
  function normalize(value) {
    if (typeof value !== 'string' || !value) return value;
    return value.replace(TASHKEEL, '').replace(TATWEEL, '').replace(/\s+/g, ' ').trim();
  }

  /* THE FALLBACK. Letter-level folds, for a query the index answered with nothing. */
  function fold(value) {
    if (typeof value !== 'string' || !value) return value;
    return normalize(value)
      .replace(ALEF, 'ا') /* alef */
      .replace(/ة/g, 'ه') /* ta marbuta -> ha */
      .replace(/[ىی]/g, 'ي') /* alef maksura / farsi yeh -> ya */
      .replace(/ک/g, 'ك'); /* keheh -> kaf */
  }

  /* ══ WHICH WAY THE FALLBACK HAS TO POINT (measured on this store's own index, 2026-09-19) ══
     The fold alone would have been useless in the direction that actually costs sales. Measured
     against /search/suggest on the dev proxy:

         أبيض  -> 7 options      ابيض  -> 0        إبيض -> 0
         قميص  -> 7              قميص ابيض -> 6    بولو -> 7

     Shopify's index stores SVNTY's own spelling — `أبيض`, with the hamza. So the shopper who gets
     nothing is the one who typed the BARE alef, which is how most Egyptians type: they omit the
     hamza. Folding their `ابيض` gives `ابيض` again — the same zero. The useful move is the
     RESTORATION, not the fold, and it is one-to-many, so it is a short ORDERED list of candidates
     rather than a transformation:

       1. fold(term)                  — for a catalogue entry stored in the folded spelling
       2. word-initial ا -> أ         — the common omission (`ابيض` -> `أبيض`, 0 -> 7)
       3. word-initial ا -> إ         — the other one (`ازاز` -> `إزاز`)
       4. final ه -> ة                — ta-marbuta written as ha

     NOTHING IS EVER OFFERED UNVERIFIED. The typeahead tries candidates and renders the first that
     actually returns rows; the results page asks /search/suggest about each one and only then
     writes a "did you mean" link. A candidate that answers with nothing is never shown, so the
     shopper is never sent from one empty page to another. */
  function foldCandidates(term) {
    var out = [];
    function push(value) {
      if (!value || value === term) return;
      if (out.indexOf(value) > -1) return;
      out.push(value);
    }
    push(fold(term));
    push(term.replace(/(^|\s)ا/g, '$1أ'));
    push(term.replace(/(^|\s)ا/g, '$1إ'));
    push(term.replace(/ه(\s|$)/g, 'ة$1'));
    return out.slice(0, 3);
  }

  /* R2-49: the old version returned FALSE for mixed input, because a single Latin letter ended the
     scan — and `polo بولو` is most real Egyptian search traffic, so the field never flipped to RTL
     for it. An Arabic letter ANYWHERE means the string carries Arabic. */
  function isArabic(value) {
    if (typeof value !== 'string') return false;
    for (var i = 0; i < value.length; i++) {
      if (ARABIC_LETTER.test(value.charAt(i))) return true;
    }
    return false;
  }

  function syncDirection(input) {
    if (!input) return;
    var rtl = isArabic(input.value);
    var want = rtl ? 'rtl' : 'ltr';
    if (input.getAttribute('dir') !== want) input.setAttribute('dir', want);
    if (input.style.textAlign !== (rtl ? 'right' : '')) input.style.textAlign = rtl ? 'right' : '';
  }

  /* -----------------------------------------------------------------------
     RECENT SEARCHES — localStorage only, nothing leaves the browser
     ----------------------------------------------------------------------- */
  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(function (item) {
          return typeof item === 'string' && item.trim().length > 0;
        })
        .map(function (item) {
          return item.trim();
        })
        .slice(0, MAX_ITEMS);
    } catch (error) {
      return []; /* private mode / blocked storage: behave as if empty */
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    } catch (error) {
      /* storage unavailable: recent searches simply stay off */
    }
  }

  function remember(value) {
    /* the SAFE normalisation only: a recent chip must re-run the search the shopper actually made,
       so it keeps their own spelling of every letter (R2-12). */
    var query = normalize(value);
    if (!query) return;
    if (query.length > MAX_LENGTH) query = query.slice(0, MAX_LENGTH).trim();
    var lower = query.toLowerCase();
    var list = read().filter(function (item) {
      return item.toLowerCase() !== lower;
    });
    list.unshift(query);
    write(list);
    renderAll();
  }

  function forget(value) {
    var lower = String(value).toLowerCase();
    write(
      read().filter(function (item) {
        return item.toLowerCase() !== lower;
      })
    );
    renderAll();
  }

  function clearAll() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* nothing to clear */
    }
    renderAll();
  }

  function searchUrl() {
    return (window.routes && window.routes.search_url) || '/search';
  }

  /* R2-13: no `type` any more. `options[prefix]=last` stays, because it is what makes a
     part-typed last word match at all. */
  function termUrl(term) {
    return searchUrl() + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
  }

  var CLOSE_SVG =
    ((window.SVNTY && SVNTY.iconHTML) ? SVNTY.iconHTML('close', 14, 1.5) : '')  /* THE ICON LAW (D11-3) */;

  function renderOne(root) {
    var chips = root.querySelector('[data-svnty-recent-chips]');
    if (!chips) return;
    var clear = root.querySelector('[data-svnty-recent-clear]');
    var list = read();

    while (chips.firstChild) chips.removeChild(chips.firstChild);

    list.forEach(function (term) {
      var chip = document.createElement('span');
      chip.className = 'svnty-search__recent';

      var link = document.createElement('a');
      link.className = 'svnty-search__recent-term';
      link.href = termUrl(term);
      link.textContent = term;
      if (isArabic(term)) link.setAttribute('dir', 'rtl');
      chip.appendChild(link);

      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'svnty-search__recent-x';
      x.setAttribute('data-svnty-recent-remove', term);
      x.setAttribute('aria-label', fill('remove_recent', '[term]', term));
      x.innerHTML = CLOSE_SVG;
      chip.appendChild(x);

      chips.appendChild(chip);
    });

    root.hidden = list.length === 0;
    /* R2-14: a HIDDEN control must not be focusable. `getFocusableElements` (assets/global.js:1)
       matches `button:enabled` with no visibility test at all, so this button stayed in the search
       sheet's focus trap while `[hidden]` — a Tab stop with nothing on screen, and a candidate for
       the trap's `first` / `last`. `disabled` is the one state that selector respects. */
    if (clear) {
      clear.hidden = list.length === 0;
      clear.disabled = list.length === 0;
    }
  }

  function renderAll() {
    var nodes = document.querySelectorAll('svnty-recent-searches');
    for (var i = 0; i < nodes.length; i++) renderOne(nodes[i]);
  }

  /* S-8 — TWO TABS ARE ONE SHOPPER. localStorage is shared across the origin but nothing tells a
     tab that another one wrote to it, so a search made in tab B, or a Clear pressed there, left tab
     A's chips stale until a reload — and pressing Clear in one tab then reopening the sheet in the
     other showed the "cleared" terms again. The `storage` event fires in every OTHER tab of the
     origin, which is exactly the set that needs repainting. `event.key === null` is a whole-storage
     clear (a shopper clearing site data), which also concerns these chips.
     The sibling's own pair: assets/sib-search-recent.js:121-124. */
  window.addEventListener('storage', function (event) {
    if (!event || event.key === STORAGE_KEY || event.key === null) renderAll();
  });

  /* The `customElements.get` guard is what makes a section re-render safe (the sibling JS-04). */
  if (!window.customElements.get('svnty-recent-searches')) {
    window.customElements.define(
      'svnty-recent-searches',
      class SvntyRecentSearches extends HTMLElement {
        connectedCallback() {
          renderOne(this);
        }
      }
    );
  }

  /* Delegated, so nothing needs re-initialising after any re-render. */
  document.addEventListener('click', function (event) {
    var remove = event.target.closest && event.target.closest('[data-svnty-recent-remove]');
    if (remove) {
      event.preventDefault();
      event.stopPropagation(); /* never let details-modal read this as a close */
      forget(remove.getAttribute('data-svnty-recent-remove'));
      return;
    }

    var clear = event.target.closest && event.target.closest('[data-svnty-recent-clear]');
    if (clear) {
      event.preventDefault();
      event.stopPropagation();
      clearAll();
      return;
    }

    /* A tapped suggestion, chip or product row is a search the shopper made. */
    var link = event.target.closest && event.target.closest('a[href*="q="]');
    if (link) {
      var href = link.getAttribute('href') || '';
      if (href.indexOf(searchUrl() + '?') === 0 || href.indexOf('/search?') === 0) {
        try {
          var q = new URLSearchParams(href.split('?')[1]).get('q');
          if (q) remember(q);
        } catch (error) {
          /* an unparseable href is not worth a thrown error */
        }
      }
    }
  });

  /* -----------------------------------------------------------------------
     SUBMIT — the SAFE normalisation once, for every path, and remember the term
     ----------------------------------------------------------------------- */
  function isSearchForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    var action = form.getAttribute('action') || '';
    return action.indexOf(searchUrl()) !== -1 || action.indexOf('/search') !== -1;
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isSearchForm(form)) return;
      var input = form.querySelector('input[name="q"]:not([type="hidden"])');
      if (!input) return;
      /* R2-12: THE RAW LETTERS ARE SENT. Only marks and stray whitespace are removed here; the fold
         is the results page's fallback, not the submit path's rewrite. */
      var clean = normalize(input.value);
      if (clean !== input.value) input.value = clean;
      if (clean) remember(clean);
    },
    true
  );

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!input || input.tagName !== 'INPUT' || input.name !== 'q') return;
    syncDirection(input);
    /* R2-14's third hidden control: Trade's search-form.js only toggles a `.hidden` CLASS on the
       reset button, which `getFocusableElements` (assets/global.js:1) cannot see — so an invisible
       "Clear" stayed a Tab stop inside the search sheet's focus trap. The markup ships it
       `disabled` while the field is empty and this keeps the two in step; nothing else about
       Trade's own toggle changes. */
    var reset = input.form && input.form.querySelector('button[type="reset"]');
    if (reset) reset.disabled = input.value.length === 0;
  });

  /* -----------------------------------------------------------------------
     THE SHEET — the focus law, the scrollbar compensation, a fresh recent list
     ----------------------------------------------------------------------- */
  var scrollbarWidth = 0;

  function measureScrollbar() {
    if (document.body.classList.contains('overflow-hidden')) return;
    var width = window.innerWidth - document.documentElement.clientWidth;
    if (width > 0 && width < 40) scrollbarWidth = width;
  }

  measureScrollbar();
  window.addEventListener('resize', measureScrollbar);

  /* Measured in the capture phase, before Trade's own click handler locks the body. */
  document.addEventListener(
    'click',
    function (event) {
      if (event.target.closest && event.target.closest('.header__icon--search')) measureScrollbar();
    },
    true
  );

  function coarse() {
    return window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  }

  /* S-10's stamp. Capture, because assets/svnty-drawer.js calls preventDefault on the same event. */
  var drawerTapAt = 0;
  document.addEventListener(
    'pointerdown',
    function (event) {
      if (!event.target || !event.target.closest) return;
      if (event.target.closest('.svnty-drawer-search__input')) drawerTapAt = Date.now();
      /* D8-2, S-4: the first touch of a search door arms the rails (the section from this session's copy, then the cards).
         D8-25 (2026-09-26 15:0x, the iPhone: "when I click on the search icon nothing is happening"): on the HEADER'S
         door the first touch only WARMS the fetch; nothing is written to the page until the door has opened (the toggle
         below arms). iOS Safari cancels a tap whose page changes under the finger between its mouse-over and its click:
         measured on the simulator, 310 mutations during the tap (the sheet's rails drawn), then no mousedown, no click,
         every time; the burger, which changes nothing, clicked. The drawer's field is a text field (no click to lose). */
      if (event.target.closest('.svnty-drawer-search__input')) armRails();
      else if (event.target.closest('.header__search summary, [data-svnty-search-door]')) warmRails();
    },
    true
  );

  /* P24-1 THE SEARCH DOOR (the 404's field): a link to the results page that opens THE SEARCH SHEET instead, through
     Trade's OWN summary, so the sheet's keyboard rule, curated page, suggestions, Arabic, recents, lock and empty-search
     guard are the header's, never a second search. iOS: the press above only WARMS (the tap law); the open waits for the
     click and one frame more, because details-modal.js adds its click-outside listener during the opening click and that
     same click would close it at once. With no sheet on the page (or no script) the link simply goes to the results page.
     When the sheet closes, focus comes back to the door (never the header's icon she did not touch), without a scroll. */
  document.addEventListener('click', function (event) {
    var door = event.target && event.target.closest ? event.target.closest('[data-svnty-search-door]') : null;
    if (!door) return;
    var summary = visibleSheetSummary();
    var details = summary && summary.closest('details');
    if (!details) return;
    event.preventDefault();
    window.requestAnimationFrame(function () {
      if (details.hasAttribute('open')) return;
      summary.click();
      if (typeof MutationObserver !== 'function') return;
      var mo = new MutationObserver(function () {
        if (details.hasAttribute('open')) return;
        mo.disconnect();
        window.setTimeout(function () { try { door.focus({ preventScroll: true }); } catch (e) { /* nothing */ } }, 0);
      });
      mo.observe(details, { attributes: true, attributeFilter: ['open'] });
    });
  });

  /* -----------------------------------------------------------------------
     S-7 — ⌘K / Ctrl+K
     -----------------------------------------------------------------------
     The shortcut every shopper who uses one expects, and the one the sibling binds
     (assets/predictive-search.js:96-104). `preventDefault` is not optional: without it Chrome and
     Edge hand ⌘K/Ctrl+K to the address bar and the sheet loses the race on its own page.
     It opens through Trade's OWN `<summary>`, never by writing the `open` attribute, so
     details-modal.js sets its focus trap and its body lock exactly as a tap would — and pressing it
     again closes, because `onSummaryClick` toggles.
     THE VISIBLE ONE: the header renders the sheet twice (phone and desktop); `offsetParent` is what
     tells them apart, the same test assets/svnty-drawer.js uses. */
  function visibleSheetSummary() {
    var all = document.querySelectorAll('details-modal.header__search');
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null) return all[i].querySelector('summary');
    }
    return all.length ? all[0].querySelector('summary') : null;
  }

  document.addEventListener('keydown', function (event) {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
    var key = event.key || '';
    if (key.toLowerCase() !== 'k') return;
    var summary = visibleSheetSummary();
    if (!summary) return;
    event.preventDefault();
    summary.click();
  });

  /* -----------------------------------------------------------------------
     SR-6 — ESCAPE ON THE PAGE'S FIELD
     -----------------------------------------------------------------------
     In the sheet, Escape closes the sheet — details-modal.js owns that and this must never touch
     it. On /search the key did nothing at all. The sibling's rule (assets/search-page-input.js:20-37):
     Escape on an ALREADY-EMPTY field submits an empty search, which is the way back to the bare
     page; Escape on a field with something in it belongs to the typeahead, which closes its panel.
     Guarded on the URL actually carrying a `q`, so Escape on the bare page is not a reload. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    var input = event.target;
    if (!input || input.tagName !== 'INPUT' || input.name !== 'q') return;
    if (input.closest('.search-modal')) return; /* the sheet's Escape is the sheet's */
    if (input.value.trim() !== '') return;
    var current = '';
    try {
      current = (new URLSearchParams(window.location.search).get('q') || '').trim();
    } catch (error) {
      current = '';
    }
    if (!current) return;
    event.preventDefault();
    window.location.href = searchUrl();
  });

  /* -----------------------------------------------------------------------
     S-6 — THE ROTATING PLACEHOLDER (one engine, every field)
     -----------------------------------------------------------------------
     the sibling runs ONE typewriter over the merchant's own term list and points the sheet field, the
     drawer field and the hero pill at it (assets/sib-hero-search.js:1-33) — change the list and
     every surface changes. SVNTY's placeholder was the static word "Search", so the field never
     told a first-time shopper what this catalogue is: menswear, by category, in two languages.

     THREE RULES IT KEEPS
     1. REAL TYPING ALWAYS WINS. The sink only writes while `value === ''`, and it restores the
        plain base word the moment the shopper types, so the placeholder can never look like text
        they entered.
     2. IT DOES NOT RUN WHERE IT CANNOT BE SEEN. A closed sheet's field has no client rects; that
        instance idles at 800 ms instead of running a per-character timer. No rAF loop, no
        animation on an offscreen node.
     3. REDUCED MOTION GETS A SWAP, NOT A TYPEWRITER — the terms still rotate, in whole words.
     The base word is read from the field's own placeholder, so it stays whatever the locale says
     and no English literal enters this file. */
  var TYPE_MIN = 55;
  var TYPE_MAX = 95;
  var ERASE = 32;
  var HOLD = 1400;
  var GAP = 350;
  var IDLE = 800;
  var SWAP = 2600;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function typewrite(input) {
    if (input.svntyTypewriter) return;
    input.svntyTypewriter = true;

    /* P24-1: a search DOOR (the 404's) is not a field: its words are its own text, written the same way */
    var isField = input.tagName === 'INPUT';
    var base = ((isField ? input.getAttribute('placeholder') : input.textContent) || '').trim();
    var terms = (input.getAttribute('data-terms') || '')
      .split(',')
      .map(function (term) {
        return term.trim();
      })
      .filter(Boolean);
    if (!terms.length) return;

    var index = 0;
    var chars = 0;
    var erasing = false;
    var reduce = reducedMotion();
    /* RECO Tier 2 (RECO_TIER2_WIRING_2026-09-26.md section 3): the shopper's own SUGGESTED row feeds the rotation too */
    document.addEventListener('svnty:suggest', function (e) {
      var next = e && e.detail && Array.isArray(e.detail.terms) ? e.detail.terms.filter(Boolean) : [];
      if (next.length) { terms = next; index = index % terms.length; }
    });

    function visible() {
      return input.getClientRects().length > 0;
    }

    function paint(text) {
      /* Busy = the shopper is typing. Hand the field back its own word and touch nothing else. */
      if (isField && input.value !== '') {
        if (input.getAttribute('placeholder') !== base) input.setAttribute('placeholder', base);
        return false;
      }
      if (isField) input.setAttribute('placeholder', text ? base + ' ' + text : base);
      else input.textContent = text ? base + ' ' + text : base;
      return true;
    }

    function step() {
      if (!visible()) {
        window.setTimeout(step, IDLE);
        return;
      }

      if (reduce) {
        var ok = paint(terms[index]);
        index = (index + 1) % terms.length;
        window.setTimeout(step, ok ? SWAP : IDLE);
        return;
      }

      var term = terms[index];
      var wrote = paint(term.slice(0, chars));
      if (!wrote) {
        window.setTimeout(step, IDLE);
        return;
      }

      if (!erasing) {
        if (chars < term.length) {
          chars += 1;
          window.setTimeout(step, TYPE_MIN + Math.random() * (TYPE_MAX - TYPE_MIN));
        } else {
          erasing = true;
          window.setTimeout(step, HOLD);
        }
        return;
      }

      if (chars > 0) {
        chars -= 1;
        window.setTimeout(step, ERASE);
        return;
      }

      erasing = false;
      index = (index + 1) % terms.length;
      window.setTimeout(step, GAP);
    }

    window.setTimeout(step, GAP);
  }

  function typewriteAll() {
    var nodes = document.querySelectorAll('input[data-svnty-typewriter], [data-svnty-search-door] [data-svnty-typewriter]');
    for (var i = 0; i < nodes.length; i++) typewrite(nodes[i]);
  }

  /* -----------------------------------------------------------------------
     D8-2 THE RAILS LOADER (R229, 2026-09-26)
     -----------------------------------------------------------------------
     The sheet's rails (PICKED FOR YOU, RECENTLY VIEWED, BEST SELLERS on THE CARD v2) live in
     sections/svnty-sheet-rails.liquid, fetched ONCE per page through the Section Rendering API
     (routes.search_url + ?section_id=, the address on the foot slot) at idle, or sooner on the
     first touch of a search door, and seated in the sheet's two slots: the picked part at the
     top, the rest at the foot. The fetched markup's stylesheets and scripts join <head> once
     (the taste engine, svnty-taste.js, lives only on the product and collection templates: the
     other pages get it here). Arming = the taste engine draws its two lazy roots (their cards
     through svnty-card-fetch, cached per page), then the row law and the arrows measure the
     rails on the next frame; it runs at idle so the first open is whole, and again on every
     open so the history is never stale. A failed fetch leaves the sheet as it was: chips only. */
  /* S-4 (lane SHEET_S, 2026-09-26): THE ONE FETCH PER SESSION. The section (the catalogue walk, about 90 KB) is kept in
     this tab's sessionStorage with its stamp and served from there for 10 minutes: a page view fetches it only on a miss,
     and nothing else leaves the page at idle (the roots' cards and the catalogue index wait for the first touch of a search
     door, which the open follows by a tenth of a second). A failed fetch leaves the sheet as it was: chips only. */
  var railsP = null, RAILS_KEY = 'svnty:sheet-rails:v2', RAILS_TTL = 10 * 60 * 1000;
  function railsFoot() { return document.querySelector('[data-svnty-sheet-slot="foot"]'); }
  function railsCached(src) {
    try {
      var c = JSON.parse(window.sessionStorage.getItem(RAILS_KEY) || 'null');
      if (c && c.src === src && typeof c.html === 'string' && Date.now() - c.at < RAILS_TTL) return c.html;
    } catch (e) { /* storage refused: fetch */ }
    return null;
  }
  function railsKeep(src, html) {
    try { window.sessionStorage.setItem(RAILS_KEY, JSON.stringify({ at: Date.now(), src: src, html: html })); } catch (e) { /* full or refused */ }
  }
  /* P28 lane E (launch audit async P2-2): THE RAILS' ONE ASK, honest. A refused answer (429 or 5xx) is asked once more after
     Retry-After (capped 4 s); a non-ok answer throws; the callers forget a failed ask (warmP / railsP back to null), so the next
     open of the sheet asks again instead of keeping "chips only" for the page's life. */
  function railsGet(src, tries) {
    tries = tries || 0;
    return fetch(src, { credentials: 'same-origin' }).then(function (r) {
      if ((r.status === 429 || r.status >= 500) && tries < 1) {
        var after = parseFloat(r.headers.get('Retry-After'));
        return new Promise(function (ok) { setTimeout(ok, Math.min(after > 0 ? after * 1000 : 900, 4000)); }).then(function () { return railsGet(src, tries + 1); });
      }
      if (!r.ok) throw new Error('rails ' + r.status);
      return r.text();
    });
  }
  /* D8-25: the fetch alone, into this session's copy; no DOM. loadRails() then finds the copy and inserts without a wait. */
  var warmP = null;
  function warmRails() {
    if (warmP || railsP) return warmP || railsP;
    var foot = railsFoot();
    var src = foot && foot.getAttribute('data-svnty-sheet-src');
    if (!src || typeof fetch !== 'function' || railsCached(src)) return (warmP = Promise.resolve(true));
    warmP = railsGet(src)
      .then(function (t) { if (t && t.indexOf('data-svnty-sheet-rails') >= 0) railsKeep(src, t); warmP = null; return !!t; })
      .catch(function () { warmP = null; return false; });
    return warmP;
  }
  function loadRails() {
    if (railsP) return railsP;
    var foot = railsFoot();
    var src = foot && foot.getAttribute('data-svnty-sheet-src');
    if (!src || typeof fetch !== 'function' || typeof DOMParser !== 'function') return (railsP = Promise.resolve(false));
    var kept = railsCached(src);
    railsP = (kept ? Promise.resolve(kept) : railsGet(src)
      .then(function (t) { if (t && t.indexOf('data-svnty-sheet-rails') >= 0) railsKeep(src, t); return t; }))
      .then(function (t) {
        var body = t && new DOMParser().parseFromString(t, 'text/html').querySelector('[data-svnty-sheet-rails]');
        if (!body) { railsP = null; return false; }   /* not the rails (an error page): the next open asks again */
        var head = document.head, i, l, s;
        var links = body.querySelectorAll('link[rel="stylesheet"]');
        for (i = 0; i < links.length; i++) {
          var href = links[i].getAttribute('href');
          links[i].parentNode.removeChild(links[i]);
          if (href && !document.querySelector('link[rel="stylesheet"][href="' + href + '"]')) { l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href; head.appendChild(l); }
        }
        var scripts = body.querySelectorAll('script[src]');
        for (i = 0; i < scripts.length; i++) {
          var ssrc = scripts[i].getAttribute('src');
          scripts[i].parentNode.removeChild(scripts[i]);
          if (ssrc && !document.querySelector('script[src="' + ssrc + '"]')) { s = document.createElement('script'); s.src = ssrc; s.defer = true; head.appendChild(s); }
        }
        var top = document.querySelector('[data-svnty-sheet-slot="top"]');
        var mid = document.querySelector('[data-svnty-sheet-slot="mid"]');
        var parts = body.querySelectorAll('[data-svnty-sheet-part]');
        for (i = 0; i < parts.length; i++) {
          var kind = parts[i].getAttribute('data-svnty-sheet-part');
          var host = kind === 'picked' ? top : kind === 'facets' ? mid : foot;
          if (host) host.appendChild(document.importNode(parts[i], true));
        }
        /* S-3: the top slot shows only once PICKED FOR YOU has drawn (settleTop), never an empty or half drawn root */
        if (mid && mid.childElementCount) mid.hidden = false;
        foot.hidden = false;
        try { document.dispatchEvent(new CustomEvent('svnty:sheet:rails')); } catch (e) {}
        return true;
      })
      .catch(function () { railsP = null; return false; });
    return railsP;
  }
  /* S-3 (lane SHEET_S): THE HELD PLACE. On an open before PICKED FOR YOU has drawn, the top slot holds the rail's height
     (this session's measure at this width, else the rail's own geometry: the card's picture at the rail's per view width
     plus the card's text block) and stays empty until the root has its cards, then the root paints whole in one write; the
     chips under it never move. A root that ends hidden (a thin history) lets the slot go. */
  function topSlot() { return document.querySelector('[data-svnty-sheet-slot="top"]'); }
  function topKey() { return 'svnty:sheet-top-h:' + Math.round(window.innerWidth / 10) + 'x' + (window.innerHeight < 500 ? 's' : 't'); }
  /* P17-19 (the eye pass's P2-16): the held place is kept only once the shopper has reached into the sheet (a press, a wheel,
     a touch move or a key since this open; never a scroll event, which the rails' own placing fires); an untouched sheet lets
     a rail shorter than the guess close up when it draws, so the first open no longer leaves 90 to 110 px of paper under
     PICKED FOR YOU. The measure is also kept on the device (localStorage) as well as the session, so the guess is the rail's
     real height from the second visit on. */
  var topTouched = false;
  ['pointerdown', 'wheel', 'touchmove', 'keydown'].forEach(function (t) {
    document.addEventListener(t, function (e) {
      var el = e.target;
      if (el && el.nodeType !== 1) el = el.parentElement;
      if (el && el.closest && el.closest('.search-modal')) topTouched = true;
    }, { capture: true, passive: true });
  });
  function reserveH(top) {
    try { var kept = parseInt(window.sessionStorage.getItem(topKey()), 10); if (kept > 200) return kept; } catch (e) {}
    try { var held = parseInt(window.localStorage.getItem(topKey()), 10); if (held > 200) return held; } catch (e) {}
    var w = top.clientWidth || window.innerWidth, col = parseFloat(getComputedStyle(top).getPropertyValue('--svnty-search-col')) || 16;
    /* the per view count follows svnty-search.css (S-8, S-9): two on the phone (of the window's width), three from 750 and
       four on a phone on its side (of the sheet's own width); the text block is the learning state's, the tallest */
    var per = window.innerHeight < 500 && window.innerWidth > window.innerHeight ? 4 : window.innerWidth >= 750 ? 3 : 2;
    var card = per === 2 ? (window.innerWidth - 2 * col - 12) / 2 : (w - 2 * col - (per - 1) * 12) / per;
    return Math.round(card + 347); /* the card's picture is square and the learning state's words measure 347 px with the card's own rows: the old 4:5 guess held 49 to 77 px too much and the rows under it jumped up on the first open */
  }
  function reserveTop() {
    var top = topSlot();
    topTouched = false;
    if (!top || top.__svntyDrawn) return;
    top.hidden = false;
    top.setAttribute('data-svnty-pending', '');
    top.style.minHeight = reserveH(top) + 'px';
  }
  function settleTop() {
    var top = topSlot();
    if (!top) return;
    var pk = top.querySelector('[data-svnty-picked]'), shown = !!(pk && !pk.hidden);
    if (shown) {
      var h = Math.round(pk.getBoundingClientRect().height);
      if (h > 200) {
        try { window.sessionStorage.setItem(topKey(), String(h)); } catch (e) {}
        try { window.localStorage.setItem(topKey(), String(h)); } catch (e) {}
      }
      top.__svntyDrawn = true;
    }
    /* while this open lasts, a rail shorter than the held place keeps the place once the shopper has reached into the sheet
       (the chips never move up under the finger either); untouched, the place closes up at once (P17-19); the close lets it
       go (releaseTop) */
    var held = parseFloat(top.style.minHeight) || 0, open = !!top.closest('details[open]');
    if (!(shown && open && held && topTouched && pk.getBoundingClientRect().height <= held)) top.style.minHeight = '';
    top.removeAttribute('data-svnty-pending');
    top.hidden = !shown;
  }
  function releaseTop() { var top = topSlot(); if (top && !top.hasAttribute('data-svnty-pending')) top.style.minHeight = ''; }
  /* ARMING: the taste engine draws the sheet's two roots (their cards through svnty-card-fetch, cached per page), then the
     row law and the arrows measure. One arm at a time: the door's first touch starts it and the open that follows joins it
     (a second arm would redraw the roots under the first); every later open arms again, so the history is never stale. */
  var armP = null;
  function armRails() {
    if (armP) return armP;
    armP = loadRails().then(function (ok) {
      if (!ok) return false;
      return new Promise(function (res) {
        (function go(n) {
          var T = window.SVNTY && window.SVNTY.taste;
          if (!T || typeof T.arm !== 'function') { if (n < 40) setTimeout(function () { go(n + 1); }, 150); else res(false); return; }
          Promise.resolve(T.arm(document.querySelector('.search-modal') || document, false)).then(function () { res(true); }, function () { res(true); });
        })(0);
      });
    }).then(function (ok) {
      armP = null;
      settleTop();
      requestAnimationFrame(function () {
        try { window.SVNTY.rows.measure(); } catch (e) {}
        try { window.SVNTY.railNav.place(); } catch (e) {}
      });
      return ok;
    });
    return armP;
  }
  function scheduleRails() {
    if (!railsFoot()) return;
    // CL20 (D9-4): the sheet's rails (about 120 nodes) seat only after 400 ms without a scroll, wheel, touch or press, so a
    // swipe on a collection rail never shares a frame with the insert (proven 4 of 4 cold runs, part8-work/taste-consent/probe5.py)
    var still = Date.now(), T = ['scroll', 'wheel', 'touchmove', 'pointerdown'];
    function mark() { still = Date.now(); }
    T.forEach(function (t) { window.addEventListener(t, mark, { capture: true, passive: true }); });
    var go = function () {
      var q = 400 - (Date.now() - still);
      if (q > 0) return setTimeout(go, q);
      T.forEach(function (t) { window.removeEventListener(t, mark, { capture: true, passive: true }); });
      loadRails();
    };
    if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 4000 }); else setTimeout(go, 1500);
  }

  var openY = null;   /* the page's place when the sheet opened (D6-13) */
  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target;
      if (!details || details.tagName !== 'DETAILS') return;
      var modal = details.closest('.header__search');
      if (!modal) return;

      var sheet = details.querySelector('.search-modal');

      if (!details.hasAttribute('open')) {
        document.body.style.paddingRight = '';
        /* D6-13 (2026-09-25 22:33, iPhone: "when I close the search page it scrolls to the top on its own"): the page
           keeps the place it had when the sheet opened; on iOS the keyboard's dismissal under the lock can throw the scroll to
           the top a few frames after the close, so the place is restored on the next frame and twice more within half a second */
        var keepY = openY;
        if (keepY !== null) {
          var restore = function () { if (Math.abs(window.scrollY - keepY) > 1) window.scrollTo({ top: keepY, left: 0, behavior: 'instant' }); };
          requestAnimationFrame(restore); setTimeout(restore, 120); setTimeout(restore, 450);
        }
        openY = null;
        /* S-4 — THE SHEET ALWAYS REOPENS ON ITS CURATED PAGE.
           `details-modal.js` only removes the `open` attribute: the field kept the term, the panel
           kept `results`, and on a mouse the next open focused the field, whose `onFocus` saw a term
           it had already answered and re-opened the previous answer over RECENT / BROWSE /
           SUGGESTED. snippets/header-search.liquid states the contract in its own header; this is
           what keeps it. The reset lives on the controller (`resetForClose`, assets/predictive-
           search.js) because only it owns the abort controller, the cache key and the term. */
        var closing = details.querySelector('predictive-search');
        if (closing && typeof closing.resetForClose === 'function') closing.resetForClose();
        if (sheet) sheet.classList.remove('is-from-drawer');
        releaseTop();
        return;
      }

      openY = window.scrollY;
      if (scrollbarWidth) document.body.style.paddingRight = scrollbarWidth + 'px';
      renderAll();
      /* D8-2: the rails re-read the shopper's history on every open (a PDP visited in another tab, a wishlist
         change, a cleared list), so the sheet never opens on a stale row. S-3: a first open holds PICKED's place. */
      reserveTop();
      armRails();

      /* S-10 — THE ICON TELLS THE TRUTH ABOUT WHERE CLOSING GOES.
         assets/svnty-drawer.js returns the shopper to the menu drawer when a sheet opened FROM the
         drawer closes (its `cameFromDrawer` latch), so the X promised "dismiss" and delivered
         "back". The sibling marks the same case with `.is-from-drawer` on its dialog
         (snippets/search-modal.liquid:82-85, CSS :554-556). The latch is private to the drawer's
         closure, so this lane keeps its own: the drawer opens the sheet from a `pointerdown` on
         `.svnty-drawer-search__input`, and that tap is a fact this file can observe without
         reaching into another lane's file. */
      if (sheet && Date.now() - drawerTapAt < 1200) sheet.classList.add('is-from-drawer');

      var panel = details.querySelector('.search-modal__content');
      if (!panel) return;
      var input = panel.querySelector('input[name="q"]:not([type="hidden"])');
      if (input) syncDirection(input);

      /* THE FOCUS LAW: the close on touch, the field on a mouse. */
      var close = panel.querySelector('.search-modal__close-button');
      if (coarse()) {
        if (close) {
          if (input) input.blur();
          close.focus({ preventScroll: true });
        }
      } else if (input) {
        input.focus({ preventScroll: true });
      }
      /* S-6 (lane SHEET_S, 2026-09-26): opened from the burger drawer's field, the drawer's own close runs after this
         open (Trade's closeMenuDrawer removes every focus trap and hands the focus to the burger), so the sheet takes its trap
         and its focus back once the drawer has let go (checked over the drawer's 400 ms close) */
      if (Date.now() - drawerTapAt < 1200 && typeof trapFocus === 'function') {
        var retake = function () {
          if (!details.hasAttribute('open') || panel.contains(document.activeElement)) return;
          trapFocus(panel.closest('[tabindex="-1"]') || panel, coarse() ? close || input : input || close);
        };
        [0, 150, 450, 800].forEach(function (ms) { setTimeout(retake, ms); });
      }
    },
    true
  );

  /* -----------------------------------------------------------------------
     THE PREFIX GUARD — every facet and sort form on /search (R2-13, R2-40)
     -----------------------------------------------------------------------
     Trade's facets.liquid carries `q` into its forms but not `options[prefix]`, so filtering or
     sorting a search silently changed the match rule under the shopper. One hidden field per form
     fixes it — and `type` is NO LONGER injected, because the results page renders pages and
     articles too (see §4 of the header).

     R2-40: this used to run on every document-wide `input`, `change` and `click`, in CAPTURE — three
     listeners re-walking the DOM on every keystroke anywhere on the page. The only thing that can
     make the fields disappear is facets.js replacing the container's markup, so the container is
     what is watched. A MutationObserver callback is a microtask at the end of the task that
     re-rendered, which is always before the shopper's next event, so the field is never missing when
     Trade's delegated handler reads the form. */
  function injectPrefix(page) {
    var forms = page.querySelectorAll('form');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      if (form.querySelector('input[name="options[prefix]"]')) continue;
      var prefix = document.createElement('input');
      prefix.type = 'hidden';
      prefix.name = 'options[prefix]';
      prefix.value = 'last';
      form.appendChild(prefix);
    }
  }

  function armPrefixGuard() {
    var page = document.querySelector('[data-svnty-search-facets]');
    if (!page) return;
    injectPrefix(page);
    if (page.svntyPrefixWatched) return;
    page.svntyPrefixWatched = true;
    new MutationObserver(function () {
      injectPrefix(page);
    }).observe(page, { childList: true, subtree: true });
  }

  /* -----------------------------------------------------------------------
     THE TROUBLE WATCHDOG (R2-15)
     -----------------------------------------------------------------------
     The state machine, stated once so it cannot drift again:
       `loading` ADDED            -> arm the 4 s timer
       `results` ADDED            -> a real answer arrived: disarm and hide
       `loading` REMOVED, and the element carries no `results` -> the request DIED
                                    (predictive-search.js calls close() on a failed fetch, which is
                                    what removes `loading`): show the sentence NOW, do not wait
       retry pressed              -> hide, and re-fire the input so the controller tries again */
  function watch(element) {
    if (element.svntyWatched) return;
    element.svntyWatched = true;

    var timer = null;
    var trouble = element.querySelector('[data-svnty-search-trouble]');
    if (!trouble) return;
    var retry = trouble.querySelector('[data-svnty-search-retry]');

    var troubleText = trouble.querySelector('[data-svnty-search-trouble-text]');

    /* R3-29 (round 3): one node, three sentences. A forced 429 and a forced 500 both used to render
       "Search is taking a moment. Try again" the instant they landed - a bot-check and a server error
       told the shopper her connection was slow. `assets/predictive-search.js` writes the kind onto the
       element (`data-svnty-search-fail`), the timeout road leaves it unset and keeps the original
       sentence, and the words themselves come from the locale through window.svntyStrings. */
    function show() {
      if (troubleText) {
        var kind = element.dataset.svntySearchFail;
        var STR = window.svntyStrings || {};
        var line = kind === 'busy' ? STR.search_busy : kind === 'down' ? STR.search_down : STR.search_slow;
        if (line) troubleText.textContent = line;
      }
      trouble.hidden = false;
      /* R2-14's pair: the retry is only a Tab stop while it is on screen. */
      if (retry) retry.disabled = false;
    }

    function disarm() {
      if (timer) window.clearTimeout(timer);
      timer = null;
      delete element.dataset.svntySearchFail;   /* R3-29: a fresh attempt is not the last one's failure */
      trouble.hidden = true;
      if (retry) retry.disabled = true;
    }

    function arm() {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(show, TROUBLE_MS);
    }

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var name = records[i].attributeName;
        if (name === 'loading') {
          if (element.hasAttribute('loading')) {
            arm();
          } else if (element.hasAttribute('results')) {
            disarm();
          } else {
            /* loading gone with nothing to show for it: the failure this sentence exists for */
            if (timer) window.clearTimeout(timer);
            timer = null;
            show();
          }
        }
        if (name === 'results' && element.hasAttribute('results')) disarm();
      }
    }).observe(element, { attributes: true, attributeFilter: ['loading', 'results'] });

    trouble.addEventListener('click', function (event) {
      if (!event.target.closest('[data-svnty-search-retry]')) return;
      event.preventDefault();
      event.stopPropagation();
      disarm();
      var input = element.querySelector('input[name="q"]:not([type="hidden"])');
      if (input) input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  function watchAll() {
    var nodes = document.querySelectorAll('predictive-search');
    for (var i = 0; i < nodes.length; i++) watch(nodes[i]);
  }

  /* -----------------------------------------------------------------------
     R2-12 — THE RESULTS PAGE'S DID-YOU-MEAN, VERIFIED BEFORE IT IS OFFERED
     -----------------------------------------------------------------------
     sections/main-search.liquid renders an empty `[data-svnty-search-fold]` placeholder on a
     zero-result page and hands it the term. Liquid can run the candidate spellings but it cannot
     run a second search, so it cannot know whether any of them has an answer — and a "did you
     mean" that leads to another empty page is worse than no line at all. This asks
     /search/suggest about each candidate, in order, and writes the link for the FIRST one that
     returns rows. At most three small requests, only on a page that already has nothing to show,
     and if every candidate is empty the placeholder stays hidden and nothing is claimed. */
  function suggestHasRows(term) {
    var url =
      '/search/suggest?q=' +
      encodeURIComponent(term) +
      '&section_id=predictive-search&resources%5Btype%5D=product,collection,query&resources%5Blimit%5D=4';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (text) {
        return text.indexOf('role="option"') > -1;
      })
      .catch(function () {
        return false;
      });
  }

  function offerFold() {
    var host = document.querySelector('[data-svnty-search-fold]');
    if (!host || host.svntyFoldAsked) return;
    host.svntyFoldAsked = true;
    var term = normalize(host.getAttribute('data-terms') || '');
    if (!term) return;
    var candidates = foldCandidates(term);
    if (!candidates.length) return;

    function tryNext(i) {
      if (i >= candidates.length) return;
      var candidate = candidates[i];
      suggestHasRows(candidate).then(function (hit) {
        if (!hit) {
          tryNext(i + 1);
          return;
        }
        /* R3-8 (round 3): `dir="rtl"` on the whole <a> reordered the ENGLISH sentence around the
           Arabic term - measured per-glyph, the shopper read "instead ”أبيض“ Try". Only the TERM
           carries direction. The sentence stays LTR and the term goes in a <bdi>, which isolates
           its bidi run without touching the words around it. */
        var link = document.createElement('a');
        link.href = termUrl(candidate);
        link.setAttribute('dir', 'ltr');
        var tmpl = String(STR.did_you_mean || '');
        var parts = tmpl.split('[terms]');
        link.textContent = '';
        link.appendChild(document.createTextNode(parts[0] || ''));
        var term = document.createElement('bdi');
        term.textContent = candidate;
        if (isArabic(candidate)) term.setAttribute('dir', 'rtl');
        link.appendChild(term);
        link.appendChild(document.createTextNode(parts.slice(1).join('[terms]')));
        host.textContent = '';
        host.appendChild(link);
        host.hidden = false;
      });
    }
    tryNext(0);
  }

  /* S-23 (lane SHEET_S): the SUGGESTED row is one of the sheet's chip rails (assets/svnty-search.css THE CHIP RAILS); its chips
     take the rail arrows on a pointer desktop like the facet rails the section prints with the mark */
  function markRails() {
    var rows = [];
    try { rows = document.querySelectorAll('details-modal .svnty-search__default > .svnty-suggest > .svnty-search__chips'); } catch (e) { return; }
    Array.prototype.forEach.call(rows, function (r) { if (!r.hasAttribute('data-svnty-rail')) r.setAttribute('data-svnty-rail', ''); });
  }

  function boot() {
    markRails();
    renderAll();
    watchAll();
    armPrefixGuard();
    offerFold();
    typewriteAll();
    scheduleRails();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', boot);

  window.svntySearch = {
    loadRails: loadRails,
    normalize: normalize,
    fold: fold,
    foldCandidates: foldCandidates,
    isArabic: isArabic,
    remember: remember,
  };
})();
